import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Users, Heart, Trash2, Send, Sparkles, MessageCircle, ShieldCheck, Loader2,
  Trophy, Flame, BookOpen,
} from 'lucide-react';

const CATEGORIES = [
  { id: 'general', label: 'General' },
  { id: 'win', label: 'Wins' },
  { id: 'struggle', label: 'Struggles' },
  { id: 'accountability', label: 'Accountability' },
  { id: 'gratitude', label: 'Gratitude' },
];

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [prompt, setPrompt] = useState(null);
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, dp] = await Promise.all([
        api.get('/community/posts'),
        api.get('/community/daily-prompt'),
      ]);
      setPosts(p.data || []);
      setPrompt(dp.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!user) return;
    if (!content.trim() || content.trim().length < 3) { setErr('Say a little more \u2014 at least 3 characters.'); return; }
    setSubmitting(true); setErr('');
    try {
      const r = await api.post('/community/posts', { content: content.trim(), category });
      setPosts([r.data, ...posts]);
      setContent('');
    } catch (e2) { setErr(e2?.response?.data?.detail || 'Could not post.'); }
    finally { setSubmitting(false); }
  };

  const toggleKudos = async (postId) => {
    if (!user) return;
    try {
      const r = await api.post(`/community/posts/${postId}/kudos`);
      setPosts(posts.map(p => p.id === postId ? { ...p, kudos_count: (p.kudos_count || 0) + (r.data.given ? 1 : -1), _kudoed: r.data.given } : p));
    } catch {}
  };

  const remove = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    await api.delete(`/community/posts/${postId}`);
    setPosts(posts.filter(p => p.id !== postId));
  };

  const filtered = filter === 'all' ? posts : posts.filter(p => p.category === filter);

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative bg-ink text-white py-14 md:py-20 overflow-hidden" data-testid="community-hero">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(700px 400px at 30% 50%, rgba(212,164,55,0.15), transparent 60%)' }} />
        <div className="relative kc-container grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold"><Users size={12} /> The Community</div>
            <h1 className="mt-4 font-display uppercase leading-[0.9]" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }} data-testid="community-title">YOU ARE NOT<br /><span className="text-[color:var(--kc-gold)]">ALONE.</span></h1>
            <p className="mt-4 text-lg text-white/80 max-w-xl leading-relaxed">Honest. Supportive. No fake positivity. No judgment. A space to share wins, struggles, and the daily work of building a stronger mind.</p>
            <div className="mt-5 flex items-center gap-3 text-sm text-white/65"><ShieldCheck size={16} className="text-[color:var(--kc-gold)]" /> Respectful by default. No spam. No drama.</div>
          </div>
          {/* Daily prompt card */}
          <div className="lg:col-span-5">
            <div className="bg-white/5 backdrop-blur-md border border-[color:var(--kc-gold)]/40 rounded-[var(--kc-radius-md)] p-5">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold"><Sparkles size={12}/> Today’s prompt</div>
              <p className="mt-3 text-lg leading-relaxed text-white/90" data-testid="community-daily-prompt">{prompt?.prompt || 'Loading…'}</p>
              <button onClick={() => { setCategory('general'); setContent(prompt?.prompt ? `[Daily prompt] ${prompt.prompt}\n\n` : ''); document.querySelector('[data-testid=community-composer]')?.scrollIntoView({ behavior: 'smooth' }); }} className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[color:var(--kc-gold)] hover:text-[color:var(--kc-gold-2)]" data-testid="community-prompt-use">Answer it →</button>
            </div>
          </div>
        </div>
      </section>

      {/* COMPOSER */}
      <section className="bg-white py-8 border-b border-kc" data-testid="community-composer">
        <div className="kc-container">
          {!user ? (
            <div className="text-center py-8 bg-surface-2 rounded-[var(--kc-radius-md)] border border-kc">
              <h3 className="font-display uppercase text-2xl">Want to join the conversation?</h3>
              <p className="mt-2 text-muted-kc">Create a free account to share, react, and connect.</p>
              <div className="mt-4 flex justify-center gap-2">
                <Link to="/signup" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)]">Get started</Link>
                <Link to="/login" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] border border-kc-strong hover:bg-surface-2 font-semibold">Log in</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-surface-2 border border-kc rounded-[var(--kc-radius-md)] p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-ink text-white inline-flex items-center justify-center font-bold">{(user.name || 'M')[0]}</div>
                <div>
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-[10px] text-muted-kc uppercase tracking-wider">Posting publicly to the community</div>
                </div>
              </div>
              <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share a win, a struggle, what you’re grateful for, or what you’re working on…" rows={3} maxLength={2000} className="w-full p-3 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-base focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="community-content"/>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 px-2 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-sm" data-testid="community-category">
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <span className="text-xs text-muted-kc">{content.length}/2000</span>
                {err && <span className="text-xs text-[color:var(--kc-danger)]">{err}</span>}
                <button type="submit" disabled={submitting || content.trim().length < 3} className="ml-auto h-10 px-5 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-50" data-testid="community-submit">{submitting ? <><Loader2 size={14} className="animate-spin"/> Sharing</> : <><Send size={14}/> Share</>}</button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* FEED */}
      <section className="py-10 md:py-14">
        <div className="kc-container grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Filters */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-20 space-y-1" data-testid="community-filters">
              <div className="text-[10px] tracking-[0.22em] uppercase text-muted-kc font-semibold mb-2">Filter</div>
              {[{ id: 'all', label: 'All posts' }, ...CATEGORIES].map((c) => (
                <button key={c.id} onClick={() => setFilter(c.id)} className={`w-full text-left px-3 py-2 rounded-[var(--kc-radius-sm)] text-sm transition-colors ${filter === c.id ? 'bg-ink text-white font-semibold' : 'hover:bg-surface-2 text-ink-2'}`} data-testid={`community-filter-${c.id}`}>{c.label} <span className="opacity-60 text-xs">({c.id === 'all' ? posts.length : posts.filter(p => p.category === c.id).length})</span></button>
              ))}
              <div className="mt-6 p-4 bg-surface-2 rounded-[var(--kc-radius-md)] border border-kc">
                <Flame className="text-[color:var(--kc-gold)] mb-2" size={20}/>
                <div className="font-display uppercase text-lg leading-tight">Live sessions with Mick</div>
                <p className="mt-2 text-xs text-muted-kc">Free monthly live sessions launching soon. Members get first access.</p>
              </div>
            </div>
          </aside>

          {/* Feed */}
          <div className="lg:col-span-9 space-y-4" data-testid="community-feed">
            {loading && (
              <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-surface-2 rounded-[var(--kc-radius-md)] animate-pulse"/>)}</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-16 bg-surface-2 rounded-[var(--kc-radius-md)]" data-testid="community-empty">
                <MessageCircle className="mx-auto text-muted-kc mb-3" size={32}/>
                <h3 className="font-display uppercase text-2xl">Be the first to share.</h3>
                <p className="mt-2 text-muted-kc">The feed is quiet right now. Someone has to break the silence — might as well be you.</p>
              </div>
            )}
            {filtered.map((p) => (
              <article key={p.id} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5 hover:border-[color:var(--kc-gold)] transition-colors" data-testid={`community-post-${p.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-ink text-white inline-flex items-center justify-center font-bold">{(p.user_name || 'M')[0]}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{p.user_name || 'Member'}</div>
                    <div className="text-[10px] text-muted-kc uppercase tracking-wider">{new Date(p.created_at).toLocaleString()} · {p.category}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${p.category === 'win' ? 'bg-gold-soft text-ink' : p.category === 'gratitude' ? 'bg-green-50 text-[color:var(--kc-success)]' : 'bg-surface-2 text-ink-2'}`}>{p.category === 'win' && <Trophy size={10} className="inline mr-1"/>}{p.category}</span>
                </div>
                <p className="mt-3 text-ink-2 leading-relaxed whitespace-pre-line">{p.content}</p>
                <div className="mt-4 flex items-center gap-4 border-t border-kc pt-3">
                  <button onClick={() => toggleKudos(p.id)} disabled={!user} className={`inline-flex items-center gap-1 text-xs font-semibold transition-colors ${p._kudoed ? 'text-[color:var(--kc-gold-2)]' : 'text-muted-kc hover:text-ink'}`} data-testid={`community-kudos-${p.id}`}>
                    <Heart size={14} className={p._kudoed ? 'fill-[color:var(--kc-gold)] text-[color:var(--kc-gold)]' : ''}/> Kudos <span className="tabular-nums">({p.kudos_count || 0})</span>
                  </button>
                  {user && (p.user_id === user.id || user.is_admin) && (
                    <button onClick={() => remove(p.id)} className="ml-auto inline-flex items-center gap-1 text-xs text-[color:var(--kc-danger)] hover:underline" data-testid={`community-del-${p.id}`}><Trash2 size={12}/> Remove</button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-ink text-white py-10">
        <div className="kc-container flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">Going deeper?</div>
            <div className="mt-1 font-display uppercase text-2xl md:text-3xl">Try the HEADSPACE course — pay what you want.</div>
          </div>
          <Link to="/courses/headspace" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)]" data-testid="community-course-cta"><BookOpen size={16}/> Start the course</Link>
        </div>
      </section>
    </div>
  );
}
